import React, { useState } from 'react';
import { 
  XMarkIcon, 
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const AddServerModal = ({ isOpen, onClose, onServerAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: 22,
    username: 'root',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [copiedCommand, setCopiedCommand] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/api/provisioning/generate-token', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setTokenData(result);
      } else {
        throw new Error(result.error || 'Erro ao gerar token');
      }
    } catch (error) {
      console.error('Erro ao gerar token:', error);
      alert('Erro ao gerar token: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCommand = (command) => {
    navigator.clipboard.writeText(command).then(() => {
      setCopiedCommand(command);
      setTimeout(() => setCopiedCommand(''), 2000);
    });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      host: '',
      port: 22,
      username: 'root',
      description: ''
    });
    setTokenData(null);
    setCopiedCommand('');
    onClose();
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {tokenData ? 'Comando de Instalação' : 'Adicionar Novo Servidor'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!tokenData ? (
            /* Formulário */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Servidor *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Servidor Produção 1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IP/Hostname (Opcional)
                  </label>
                  <input
                    type="text"
                    name="host"
                    value={formData.host}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="192.168.1.100 (detectado automaticamente se vazio)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Porta SSH
                  </label>
                  <input
                    type="number"
                    name="port"
                    value={formData.port}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="65535"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usuário
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição (Opcional)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descrição do servidor..."
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Como funciona:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Preencha os dados básicos do servidor</li>
                      <li>Clique em "Gerar Comando"</li>
                      <li>Cole e execute o comando no terminal do novo servidor</li>
                      <li>O servidor será automaticamente configurado e aparecerá na lista</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Gerando...' : 'Gerar Comando'}
                </button>
              </div>
            </form>
          ) : (
            /* Comandos de Instalação */
            <div className="space-y-6">
              {/* Info do Servidor */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Servidor: {tokenData.serverData.name}</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  {tokenData.serverData.host && (
                    <p>🌐 Host: {tokenData.serverData.host}</p>
                  )}
                  <p>👤 Usuário: {tokenData.serverData.username}</p>
                  <p>🔑 Token: {tokenData.token.substring(0, 12)}...</p>
                  <p>⏰ Expira em: {tokenData.expiresIn}</p>
                </div>
              </div>

              {/* Aviso */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Importante:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Execute o comando como <strong>root</strong> no servidor</li>
                      <li>Certifique-se que o servidor tem acesso à internet</li>
                      <li>O token expira em 1 hora</li>
                      <li>Após a instalação, o servidor aparecerá automaticamente na lista</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Comandos */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Comando de Instalação:</h4>

                {/* Comando curl */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Método recomendado (curl):</label>
                    <button
                      onClick={() => handleCopyCommand(tokenData.commands.curl)}
                      className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCommand === tokenData.commands.curl ? (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          <span>Copiado!</span>
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          <span>Copiar</span>
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-green-400 p-3 rounded text-sm overflow-x-auto">
                    {tokenData.commands.curl}
                  </pre>
                </div>

                {/* Comando wget */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Alternativo (wget):</label>
                    <button
                      onClick={() => handleCopyCommand(tokenData.commands.wget)}
                      className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCommand === tokenData.commands.wget ? (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          <span>Copiado!</span>
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          <span>Copiar</span>
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-green-400 p-3 rounded text-sm overflow-x-auto">
                    {tokenData.commands.wget}
                  </pre>
                </div>

                {/* Manual */}
                <details className="cursor-pointer">
                  <summary className="text-sm font-medium text-gray-700 hover:text-gray-900">
                    Instalação manual (clique para expandir)
                  </summary>
                  <div className="mt-2 pl-4">
                    {tokenData.commands.manual.map((line, index) => (
                      <pre key={index} className="bg-gray-100 p-2 rounded text-sm mb-1">
                        {line}
                      </pre>
                    ))}
                  </div>
                </details>
              </div>

              {/* Próximos passos */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Próximos passos:</h4>
                <ol className="list-decimal list-inside text-sm text-green-700 space-y-1">
                  <li>Cole e execute um dos comandos acima no terminal do servidor</li>
                  <li>Aguarde a instalação finalizar (2-5 minutos)</li>
                  <li>O servidor aparecerá automaticamente na lista</li>
                  <li>Feche este modal e atualize a lista se necessário</li>
                </ol>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Entendi, Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddServerModal;